/**
 * In javascript, date formatting is done by creating an instance of a `DateTimeFormat` for each
 * timezone you wanna format in. Creating one of these objects for every time we format a
 * date is expensive, so this object memoizes these instances, keyed off their timezone
 */
const formatters: { [key: string]: Intl.DateTimeFormat } = {}

/**
 * book-keeping method for keeping track of date formatters.
 *
 * if a formatter with the key `key` in `_formatters` has already been created, returns it, otherwise
 * creates a new one with the given options, saves it in `_formatters` and then returns it.
 */
function memoFmt(key: string, locale: string, opts: Intl.DateTimeFormatOptions) {
  if (formatters[key] == null) {
    formatters[key] = new Intl.DateTimeFormat(locale, opts)
  }
  return formatters[key]
}

/**
 * Returns eg "Thu" for a date "Thursday Mar 11, 2018"
 */
export function dateFmtWeekday(d: string | Date, timeZone?: string | null): string {
  return memoFmt('dateFmtWeekday-' + (timeZone || 0), 'en-US', {
    weekday: 'short',
    timeZone: timeZone || undefined,
  }).format(toDate(d))
}

/**
 * Returns eg "Thu, Mar 11, 2018"
 */
export function dateFmt(d: string | Date, timeZone?: string | null): string {
  return memoFmt('dateFmt-' + (timeZone || 0), 'en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: timeZone || undefined,
  }).format(toDate(d))
}

/**
 * Returns eg "Thu, Mar 11"
 */
export function dateNoYearFmt(d: string | Date, timeZone?: string | null): string {
  return memoFmt('dateNoYearFmt-' + (timeZone || 0), 'en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: timeZone || undefined,
  }).format(toDate(d))
}

/**
 * Returns eg "Thursday, March 11"
 */
export function dateNoYearFmtLong(d: string | Date, timeZone?: string | null): string {
  return memoFmt('dateNoYearFmtLong-' + (timeZone || 0), 'en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: timeZone || undefined,
  }).format(toDate(d))
}

/**
 * Returns eg "2:03pm". Takes a Date and timezone, eg "America/New_York".
 */
export function timeFmt(d: string | Date, timeZone?: string | null): string {
  return memoFmt('timeFmt-' + (timeZone || 0), 'en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZone: timeZone || undefined,
  })
    .format(toDate(d))
    .toLowerCase()
    .replace(' ', '')
}

/**
 * Returns eg "2pm". Takes a Date and timezone, eg "America/New_York".
 */
export function timeHourFmt(d: string | Date, timeZone?: string | null): string {
  return memoFmt('timeHourFmt-' + (timeZone || 0), 'en-US', {
    hour: 'numeric',
    hour12: true,
    timeZone: timeZone || undefined,
  })
    .format(toDate(d))
    .toLowerCase()
    .replace(' ', '')
}

export function isMorning(d: string | Date, timeZone?: string | null): boolean {
  return timeHourFmt(d, timeZone).includes('am')
}

/**
 * Returns eg "2:03pm PST". Takes a Date and timezone, eg "America/New_York".
 */
export function timeTzFmt(d: string | Date, timeZone?: string | null): string {
  return memoFmt('timeTzFmt-' + (timeZone || 0), 'en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZoneName: 'short',
    timeZone: timeZone || undefined,
  })
    .format(toDate(d))
    .replace(' AM', 'am')
    .replace(' PM', 'pm')
}

/**
 * Returns eg "Mar 11 11:55:32pm EDT"
 */
export function dateTimeSecTzFmt(d: string | Date, timeZone?: string | null): string {
  return memoFmt('dateTimeSecTzFmt-' + (timeZone || 0), 'en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true,
    timeZoneName: 'short',
    timeZone: timeZone || undefined,
  })
    .format(toDate(d))
    .replace(' AM', 'am')
    .replace(' PM', 'pm')
    .replace(', ', ' ')
}

/**
 * Returns eg "2020-01-01"
 */
export function isoDateFmt(date: Date): string {
  const isoSeconds = isoSecondsFmt(date)
  return isoSeconds.substring(0, 'YYYY-MM-DD'.length)
}

/**
 * Returns eg "2020-01-01T00:00:00Z"
 */
export function isoSecondsFmt(date: Date): string {
  const isoMillis = date.toISOString()
  if (isoMillis.length !== 'YYYY-MM-DDTHH:MM:SS.SSSZ'.length) {
    throw new Error('Unexpected date: ' + isoMillis)
  }
  return isoMillis.substring(0, 'YYYY-MM-DDTHH:MM:SS'.length) + 'Z'
}

/**
 * Returns eg "Sun, Mar 11, 11:55pm"
 */
export function dateTimeFmt(d: string | Date, timeZone?: string | null): string {
  return dateNoYearFmt(d, timeZone) + ' ' + timeFmt(d, timeZone)
}

/**
 * Returns eg "Sun, Mar 11, 11:55pm PST"
 */
export function dateTimeTzFmt(d: string | Date, timeZone?: string | null): string {
  return dateNoYearFmt(d, timeZone) + ', ' + timeTzFmt(d, timeZone)
}

/**
 * Returns eg "3/11" for March 11
 */
export function dateMDNumberFmt(d: string | Date, timeZone?: string | null): string {
  return memoFmt('dateMDNumberFmt-' + (timeZone || 0), 'en-US', {
    month: 'numeric',
    day: 'numeric',
    timeZone: timeZone || undefined,
  }).format(toDate(d))
}

/**
 * Returns eg "Mar 11" for March 11
 */
export function dateMDShortFmt(d: string | Date, timeZone?: string | null): string {
  return memoFmt('dateMDShortFmt-' + (timeZone || 0), 'en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: timeZone || undefined,
  }).format(toDate(d))
}

/**
 * Returns eg "3/11" for March 11 current year, "12/12 '17" for Dec 12 2017
 */
export function dateMDYFmt(d: string | Date, timeZone?: string | null): string {
  const yearFmt = memoFmt('dateMDYFmt-' + (timeZone || 0), 'en-US', {
    year: 'numeric',
    timeZone: timeZone || undefined,
  })
  const curYear = yearFmt.format(new Date())
  const dateYear = yearFmt.format(toDate(d))
  const isSameYear = curYear === dateYear

  const opts: Intl.DateTimeFormatOptions = {
    month: 'numeric',
    day: 'numeric',
    timeZone: timeZone || undefined,
  }
  if (!isSameYear) {
    opts.year = '2-digit'
  }
  const key = 'dateMDYFmt-' + (timeZone || 0) + '-' + (isSameYear ? 1 : 0)
  return memoFmt(key, 'en-US', opts).format(toDate(d))
}

/**
 * Returns eg "3/11 3:00pm"
 */
export function dateMDTimeFmt(d: string | Date, timeZone?: string | null): string {
  return dateMDYFmt(d, timeZone) + ' ' + timeFmt(d, timeZone)
}

/**
 * Returns eg "Tue Feb 18 2020 10:13AM CST"
 */
export function dateMDYTimeTzFmt(d: string | Date, timeZone?: string | null): string {
  return dateFmt(d, timeZone).replace(/\,/gi, '') + ' ' + timeTzFmt(d, timeZone)
}

/**
 * Returns eg "Sun Mar 11 at 11:55pm"
 */
export function dateTimeFmtWithAt(d: string | Date, timeZone?: string | null): string {
  return dateNoYearFmt(d, timeZone).replace(',', '') + ' at ' + timeFmt(d, timeZone)
}

/**
 * Takes a duration in secs, returns eg "0:31" or "1:20:59"
 */
export function fmtHMS(secs: number): string {
  const s = Math.floor(secs) % 60
  const m = Math.floor(secs / 60) % 60
  const h = Math.floor(secs / 3600)
  if (h > 0) {
    return `${h}:${fmt2digit(m)}:${fmt2digit(s)}`
  } else {
    return `${m}:${fmt2digit(s)}`
  }
}

function fmt2digit(n: number) {
  return ('' + (100 + n)).substring(1)
}

/**
 * Subtracts (after - before), returns a value in seconds
 */
export function diffSecs(after: string | Date, before: string | Date): number {
  const aMs = toDate(after).getTime()
  const bMs = toDate(before).getTime()
  return (aMs - bMs) / 1000
}

/**
 * Subtracts (after - before), returns a value in minutes
 */
export function diffMins(after: string | Date, before: string | Date): number {
  return diffSecs(after, before) / 60
}

/**
 * Subtracts (after - before), returns a value in days
 */
export function diffDays(after: string | Date, before: string | Date): number {
  const aMs = toDate(after).getTime()
  const bMs = toDate(before).getTime()
  return Math.floor((aMs - bMs) / (1000 * 3600 * 24))
}

/**
 * Subtracts (after - before), returns '10s' or '20m' etc
 */
export function diffFmt(after: string | Date, before: string | Date): string {
  const s = Math.round(diffSecs(after, before))
  return diffFmtSecs(s)
}

/**
 * Subtracts (after - before), returns '10 seconds' or '20 minutes' etc
 */
export function diffLongFmt(after: string | Date, before: string | Date): string {
  const s = Math.round(diffSecs(after, before))
  return diffLongFmtSecs(s)
}

/**
 * returns '10s' or '20m' etc
 */
export function diffFmtSecs(secs: number) {
  if (secs < 100) {
    return `${secs}s`
  }
  const m = Math.round(secs / 60)
  if (m < 100) {
    return `${m}m`
  }
  const h = Math.round(m / 60)
  if (h < 48) {
    return `${h}h`
  }
  const d = Math.round(h / 24)
  return `${d}d`
}

/**
 * returns '10 seconds' or '20 minutes' etc
 */
export function diffLongFmtSecs(secs: number) {
  if (secs < 100) {
    const sUnit = secs === 1 ? 'second' : 'seconds'
    return `${secs} ${sUnit}`
  }
  const m = Math.round(secs / 60)
  if (m < 100) {
    const mUnit = m === 1 ? 'minute' : 'minutes'
    return `${m} ${mUnit}`
  }
  const h = Math.round(m / 60)
  if (h < 48) {
    const hUnit = h === 1 ? 'hour' : 'hours'
    return `${h} ${hUnit}`
  }
  const d = Math.round(h / 24)
  const dUnit = d === 1 ? 'day' : 'days'
  return `${d} ${dUnit}`
}

/**
 * returns '20m', '3h' etc. no seconds
 */
export function diffFmtSecsMinutes(secs: number) {
  if (secs < 60) {
    return `${0}m`
  }
  const m = Math.round(secs / 60)
  if (m < 100) {
    return `${m}m`
  }
  const h = Math.round(m / 60)
  if (h < 48) {
    return `${h}h`
  }
  const d = Math.round(h / 24)
  return `${d}d`
}

/**
 * Returns the number of seconds between a and b. Negative if a occurs before b, positive if a occurs after.
 */
export function secsAfter(a: string | Date, b: string | Date) {
  return (toDate(a).getTime() - toDate(b).getTime()) / 1000
}

/**
 * Takes a string like "2020-01-01T00:00:00Z" or Date, returns a Date
 */
export function toDate(d: string | Date): Date {
  if (d instanceof Date) {
    return d
  }
  return new Date(d)
}

export function areOnDifferentDays(date1: string | Date, date2: string | Date): boolean {
  return toDate(date2).getDate() !== toDate(date1).getDate()
}

/**
 * Returns true if firstDate is after secondDate and false otherwise.
 */
export function isAfter(firstDate: string | Date, secondDate: string | Date): boolean {
  return diffSecs(firstDate, secondDate) > 0
}

/**
 * Returns true if firstDate is before secondDate and false otherwise.
 */
export function isBefore(firstDate: string | Date, secondDate: string | Date): boolean {
  return diffSecs(firstDate, secondDate) < 0
}

/**
 * Returns true if secondDate is between firstDate and thirdDate (exclusive).
 */
export function isBetween(firstDate: string | Date, secondDate: string | Date, thirdDate: string | Date): boolean {
  return isBefore(firstDate, secondDate) && isBefore(secondDate, thirdDate)
}

/**
 * Adds numDays days to date and returns the resulting date.
 */
export function addDays(date: Date, numDays: number): Date {
  return new Date(date.getTime() + 1000 * 60 * 60 * 24 * numDays)
}

/**
 * Adds numMinutes minutes to date and returns the resulting date.
 */
export function addMinutes(date: string | Date, numMinutes: number): Date {
  return new Date(toDate(date).getTime() + 1000 * 60 * numMinutes)
}

/**
 * Returns a string indicating how long ago dateTime was
 * e.g. "just now", "2m ago", "1h ago", "yesterday", "2d ago", ... "
 */
export function getTimeAgo(datetime: string | Date, now: string | Date): string {
  const secsAgo = diffSecs(now, datetime)
  const minsAgo = Math.floor(secsAgo / 60)
  const hoursAgo = Math.floor(minsAgo / 60)
  if (minsAgo < 2) {
    return 'just now'
  } else if (hoursAgo < 1) {
    return minsAgo + 'm ago'
  } else if (hoursAgo <= 24) {
    return hoursAgo + 'h ago'
  }

  const daysAgo = diffDays(now, datetime)
  if (daysAgo < 2) {
    return 'yesterday'
  } else if (daysAgo < 32) {
    return daysAgo + 'd ago'
  }

  return dateNoYearFmt(datetime)
}

/**
 * Returns -1 if a is before b, 1 if after, and 0 if a and b are the same.
 */
export function compareDates(a: string | Date, b: string | Date): number {
  if (isAfter(b, a)) {
    return -1
  } else if (isAfter(a, b)) {
    return 1
  }
  return 0
}

/**
 * Returns array, sorted by date.
 */
export function sortArrayByDate(array: Date[]): Date[] {
  return array.sort((a, b) => compareDates(a, b))
}

/**
 * Get start of the day of given date.
 */
export function startOfDay(date: Date) {
  const start = new Date(date)
  start.setHours(0, 0, 0, 0)
  return start
}

/**
 * Returns true iff the supplied date is equal to today's date, false otherwise
 */
export function isToday(date: string | Date): boolean {
  const today = new Date()
  return !areOnDifferentDays(date, today)
}

/**
 * Returns true iff the supplied date is within 24 hours of now (before or after: either works)
 */
export function isWithin24HoursOfNow(date: string | Date): boolean {
  return isWithinNhoursOfNow(date, 24)
}

/**
 * Returns true iff the supplied date is within 48 hours of now (before or after: either works)
 */
export function isWithin48HoursOfNow(date: string | Date): boolean {
  return isWithinNhoursOfNow(date, 48)
}

function isWithinNhoursOfNow(date: string | Date, hour: number) {
  const nowUTCTimestamp = new Date().getTime() // in millis since 1970, UTC
  const dateUTCTimestamp = new Date(date).getTime() // same format
  return Math.abs(dateUTCTimestamp - nowUTCTimestamp) < hour * 60 * 60 * 1000
}

/**
 * Returns a string representing time based on the military hour given. e.g. 3 gives '3 AM', 22 gives '10 PM'.
 */
export function getTimeString(hour: number) {
  if (hour < 12) {
    return hour.toString() + ' AM'
  } else if (hour === 12) {
    return '12 PM'
  } else if (hour === 24) {
    return '12 AM'
  }
  return (hour - 12).toString() + ' PM'
}

/**
 * Returns the date part of an ISO date string. e.g. '2020-09-01T20:19:12.856242' gives '2020-09-01'.
 */
export function toDateString(ISODateStr: string) {
  return ISODateStr.substring(0, 10)
}

/**
 * Returns the maximum of dates a and b.
 */
export function maxDate(a: string | Date, b: string | Date) {
  if (compareDates(a, b) === -1) {
    return b
  }
  return a
}
